import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MapPin, Navigation, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { submitContact } from "@/lib/api";

const SUBJECTS = ["Demande de devis", "Urgence fuite", "Suivi de chantier", "Autre demande"];

export default function Contact() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.name && /.+@.+\..+/.test(form.email) && form.message;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || sending) return;
    setSending(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message envoyé", { description: "Nous vous répondons sous 24 h ouvrées." });
    } catch {
      toast.error("Envoi impossible", { description: "Réessayez ou appelez-nous au 02 97 38 05 40." });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-5 py-4 text-base text-roch-navy outline-none transition-colors placeholder:text-slate-400 focus:border-roch-orange";

  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact — Couverture Le Roch, Pontivy"
        siteName="LE ROCH"
        description="Contactez Couverture Le Roch à Pontivy : 02 97 38 05 40 · contact@leroch-couverture.fr. Devis gratuit, réponse sous 24 h ouvrées."
      />
      <PageHero
        kicker="Contact"
        lines={["PARLONS DE", "VOTRE TOIT."]}
        accentIndex={1}
        description="Une question, un projet de toiture ? Notre équipe vous répond sous 24 h ouvrées — devis gratuit et sans engagement."
      />

      <section className="py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.3fr] lg:gap-16 lg:px-8">
          {/* Infos */}
          <div className="flex flex-col gap-5">
            <Reveal>
              <a
                href="tel:+33297380540"
                data-testid="contact-phone-card"
                className="group flex items-center gap-5 rounded-3xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-roch-orange/50 hover:shadow-[0_20px_50px_-20px_rgba(251,195,65,0.35)]"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-roch-orange/15">
                  <Phone className="h-5 w-5 text-roch-orange" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Téléphone</span>
                  <span className="mt-1 block font-display text-lg font-bold text-roch-navy">02 97 38 05 40</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.08}>
              <a
                href="mailto:contact@leroch-couverture.fr"
                data-testid="contact-email-card"
                className="group flex items-center gap-5 rounded-3xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-roch-orange/50 hover:shadow-[0_20px_50px_-20px_rgba(251,195,65,0.35)]"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-roch-orange/15">
                  <Mail className="h-5 w-5 text-roch-orange" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Email</span>
                  <span className="mt-1 block font-display text-lg font-bold text-roch-navy">contact@leroch-couverture.fr</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="flex items-center gap-5 rounded-3xl border border-slate-200 bg-white p-6" data-testid="contact-address-card">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-roch-orange/15">
                  <MapPin className="h-5 w-5 text-roch-orange" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Adresse</span>
                  <span className="mt-1 block font-display text-lg font-bold text-roch-navy">ZA Kerponner Villeneuve Postic, 56300 Pontivy</span>
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="flex items-center gap-5 rounded-3xl border border-slate-200 bg-white p-6" data-testid="contact-zone-card">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-roch-orange/15">
                  <Navigation className="h-5 w-5 text-roch-orange" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Zone d'intervention</span>
                  <span className="mt-1 block font-display text-lg font-bold text-roch-navy">Morbihan & Côtes d'Armor</span>
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="grain relative overflow-hidden rounded-3xl bg-roch-navy p-7" data-testid="contact-devis-panel">
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-roch-orange/25 blur-[70px]" />
                <p className="relative flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-roch-orange">
                  <Send className="h-4 w-4" /> Devis gratuit
                </p>
                <p className="relative mt-3 font-display text-xl font-extrabold text-white">
                  Estimation en ligne en 2 minutes, sans engagement.
                </p>
                <Link
                  to="/devis"
                  data-testid="contact-devis-link"
                  className="relative mt-5 inline-flex items-center gap-2 rounded-full bg-roch-orange px-6 py-3 font-display text-sm font-bold text-roch-navy transition-colors hover:bg-roch-orangeHover"
                >
                  Faire ma demande de devis
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm sm:p-10" data-testid="contact-form-panel">
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="ok"
                    data-testid="contact-success-toast"
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center py-16 text-center"
                  >
                    <CheckCircle2 className="h-16 w-16 text-roch-orange" />
                    <h2 className="mt-6 font-display text-2xl font-extrabold text-roch-navy sm:text-3xl">
                      Message bien reçu.
                    </h2>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                      Merci {form.name.split(" ")[0]} — un compagnon LE ROCH vous répond sous 24 h ouvrées.
                    </p>
                    <button
                      data-testid="contact-send-another-button"
                      onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" }); }}
                      className="mt-8 rounded-full border border-slate-300 px-6 py-3 font-display text-sm font-bold text-roch-navy transition-colors hover:border-roch-orange hover:text-roch-orange"
                    >
                      Envoyer un autre message
                    </button>
                  </motion.div>
                ) : (
                  <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={submit}>
                    <h2 className="font-display text-2xl font-extrabold text-roch-navy sm:text-3xl">Écrivez-nous</h2>
                    <p className="mt-2 text-sm text-slate-600">Réponse garantie sous 24 h ouvrées.</p>

                    <div className="mt-8 grid gap-4 sm:grid-cols-2">
                      <input
                        data-testid="contact-form-name-input"
                        type="text"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Nom complet *"
                        className={inputCls}
                      />
                      <input
                        data-testid="contact-form-phone-input"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="Téléphone"
                        className={inputCls}
                      />
                      <input
                        data-testid="contact-form-email-input"
                        type="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="Email *"
                        className={`${inputCls} sm:col-span-2`}
                      />
                      <div className="flex flex-wrap gap-2 sm:col-span-2" data-testid="contact-subject-select">
                        {SUBJECTS.map((s) => (
                          <button
                            type="button"
                            key={s}
                            data-testid={`contact-subject-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            onClick={() => set("subject", s)}
                            className={`rounded-full px-4 py-2 font-display text-xs font-bold transition-all duration-300 ${
                              form.subject === s
                                ? "bg-roch-navy text-white"
                                : "border border-slate-300 text-slate-500 hover:border-roch-navy hover:text-roch-navy"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      <textarea
                        data-testid="contact-form-message-input"
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                        placeholder="Décrivez votre besoin : nature des travaux, surface, urgence… *"
                        rows={6}
                        className={`${inputCls} resize-none sm:col-span-2`}
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="contact-form-submit-button"
                      disabled={!valid || sending}
                      className="group mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-roch-orange px-8 py-4 font-display text-base font-bold text-roch-navy transition-colors duration-300 hover:bg-roch-orangeHover disabled:cursor-not-allowed disabled:opacity-30 sm:w-auto"
                    >
                      {sending ? "Envoi en cours…" : "Envoyer le message"}
                      <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
